/**
 * 迷惑メール対策（05-ui.md §8.6）。
 *
 * ドメイン単位の既定を設定する画面。判定の強さは 3 段階に丸めて見せる
 * （Rspamd のスコアしきい値をそのまま出しても契約者には判断できない）。
 *
 * 使用するエンドポイント `GET/PUT /portal/domains/:id/spam` と
 * `GET /portal/domains/:id/spam-stats` は ARCHITECTURE.md §4.3 の抜粋に
 * 含まれていない。UI 側の要求として `lib/api-types.ts` にコメント付きで定義してある。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatCard,
  SwitchRow,
  formatCount,
} from '@mail-sv/ui';
import { MailWarning, ShieldAlert, Bug } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { ErrorAlert, LoadingBlock } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote, Jargon } from '../components/help.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type { SpamPolicy, SpamStats } from '../lib/api-types.js';

const LEVEL_LABEL: Record<SpamPolicy['level'], { label: string; help: string }> = {
  low: {
    label: '弱い（誤判定を減らす）',
    help: '迷惑メールと判定される基準を厳しくします。必要なメールが迷惑メール扱いされにくくなりますが、迷惑メールも届きやすくなります。',
  },
  medium: {
    label: '標準（推奨）',
    help: 'ほとんどのお客様に適した設定です。迷った場合はこちらをお選びください。',
  },
  high: {
    label: '強い（迷惑メールを減らす）',
    help: '迷惑メールをより多く除去しますが、必要なメールが誤って判定される可能性が上がります。',
  },
};

const ACTION_LABEL: Record<SpamPolicy['action'], { label: string; help: string }> = {
  move_to_junk: {
    label: '迷惑メールフォルダに入れる（推奨）',
    help: '判定されたメールは「迷惑メール」フォルダに入ります。誤判定があっても後から取り出せます。',
  },
  tag: {
    label: '件名に [SPAM] を付けて受信する',
    help: '受信トレイには届きますが、件名で見分けられます。メールソフト側で振り分けたい場合に使います。',
  },
  reject: {
    label: '受信を拒否する',
    help: '判定されたメールは受け取りません。送信者にはエラーが返ります。誤判定のときにメールが失われるため、通常はおすすめしません。',
  },
};

export function SpamPage() {
  const domains = useDomains();
  const { user } = useAuth();
  const [domainId, setDomainId] = React.useState('');

  const list = domains.data?.items ?? [];
  const selected = domainId || list[0]?.id || '';

  return (
    <>
      <PageHeader
        title="迷惑メール対策"
        description="迷惑メール（スパム）とウイルスの判定方法をドメインごとに設定します。"
      />

      <div className="space-y-5">
        <HelpNote title="そのままでも十分に機能します">
          迷惑メールの判定は既定で有効になっており、ほとんどの迷惑メールは自動的に取り除かれます。
          必要なメールが迷惑メール扱いされる場合のみ、下の設定を調整してください。
        </HelpNote>

        {domains.isPending ? (
          <LoadingBlock lines={3} />
        ) : list.length === 0 ? (
          <Alert variant="info">
            <AlertTitle>ドメインがまだありません</AlertTitle>
            <AlertDescription>
              迷惑メールの設定はドメインごとに行います。
              <Button asChild variant="outline" size="sm" className="ml-2">
                <AppLink to="/domains">ドメインを追加する</AppLink>
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Field label="設定するドメイン" className="max-w-sm">
              <Select value={selected} onValueChange={setDomainId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {list.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {selected ? (
              <>
                <SpamStatsCards domainId={selected} />
                <SpamPolicyForm domainId={selected} canWrite={canManageOrg(user)} />
              </>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function SpamStatsCards({ domainId }: { domainId: string }) {
  const stats = useQuery({
    queryKey: qk.spamStats(domainId),
    queryFn: () => api.get<SpamStats>(`/domains/${encodeURIComponent(domainId)}/spam-stats`),
    staleTime: 5 * 60_000,
  });

  if (stats.isPending) return <LoadingBlock lines={2} />;
  if (stats.isError) return null;

  const days = stats.data?.days ?? [];
  const sum = (key: 'received' | 'spam' | 'virus') => days.reduce((a, d) => a + d[key], 0);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <StatCard label="直近 30 日の受信" value={formatCount(sum('received'))} unit="通" />
      <StatCard
        label="迷惑メールと判定"
        value={formatCount(sum('spam'))}
        unit="通"
        icon={ShieldAlert}
        tone="warning"
      />
      <StatCard
        label="ウイルス検出"
        value={formatCount(sum('virus'))}
        unit="通"
        icon={Bug}
        tone={sum('virus') > 0 ? 'destructive' : 'default'}
      />
    </div>
  );
}

function SpamPolicyForm({ domainId, canWrite }: { domainId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: qk.spamPolicy(domainId),
    queryFn: () => api.get<SpamPolicy>(`/domains/${encodeURIComponent(domainId)}/spam`),
  });

  const [draft, setDraft] = React.useState<SpamPolicy | null>(null);
  // ドメインを切り替えたら下書きを捨てて取り直した値に従う。
  React.useEffect(() => {
    setDraft(null);
  }, [domainId]);
  React.useEffect(() => {
    if (current.data && draft === null) setDraft(current.data);
  }, [current.data, draft]);

  const save = useMutation({
    mutationFn: (body: SpamPolicy) =>
      api.put<void>(`/domains/${encodeURIComponent(domainId)}/spam`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.spamPolicy(domainId) }),
  });

  if (current.isPending || draft === null) return <LoadingBlock lines={4} />;
  if (current.isError) return <ErrorAlert error={current.error} onRetry={() => current.refetch()} />;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">判定と処理の方法</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(draft);
          }}
        >
          <FormAlert error={save.error} />
          {save.isSuccess ? (
            <Alert variant="success">
              <AlertDescription>保存しました。数分以内に反映されます。</AlertDescription>
            </Alert>
          ) : null}

          <SwitchRow
            label="迷惑メールの判定を行う"
            description="オフにすると、明らかな迷惑メールも受信トレイに届くようになります。"
            checked={draft.enabled}
            disabled={!canWrite}
            onCheckedChange={(enabled) => setDraft({ ...draft, enabled })}
          />

          <Field label="判定の強さ" hint={LEVEL_LABEL[draft.level].help}>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(LEVEL_LABEL) as Array<SpamPolicy['level']>).map((lv) => (
                <Button
                  key={lv}
                  type="button"
                  variant={draft.level === lv ? 'default' : 'outline'}
                  size="sm"
                  disabled={!canWrite || !draft.enabled}
                  onClick={() => setDraft({ ...draft, level: lv })}
                >
                  {LEVEL_LABEL[lv].label}
                </Button>
              ))}
            </div>
          </Field>

          <Field label="判定されたメールの扱い" hint={ACTION_LABEL[draft.action].help}>
            <Select
              value={draft.action}
              onValueChange={(v) => setDraft({ ...draft, action: v as SpamPolicy['action'] })}
            >
              <SelectTrigger disabled={!canWrite || !draft.enabled}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ACTION_LABEL) as Array<SpamPolicy['action']>).map((a) => (
                  <SelectItem key={a} value={a}>
                    {ACTION_LABEL[a].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="常に受け取るアドレス・ドメイン（許可リスト）"
            hint="1 行に 1 件。ドメインだけを書くとそのドメイン全体が対象になります（例: example.com）。"
          >
            <textarea
              rows={4}
              value={draft.allowList.join('\n')}
              disabled={!canWrite}
              onChange={(e) => setDraft({ ...draft, allowList: splitLines(e.target.value) })}
              className="flex w-full rounded-md border border-input bg-panel-elevated px-3 py-2 font-mono text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder={'torihikisaki@example.com\nexample.co.jp'}
            />
          </Field>

          <Field
            label="常に拒否するアドレス・ドメイン（拒否リスト）"
            hint="ここに書いたアドレスからのメールは受信しません。"
          >
            <textarea
              rows={4}
              value={draft.denyList.join('\n')}
              disabled={!canWrite}
              onChange={(e) => setDraft({ ...draft, denyList: splitLines(e.target.value) })}
              className="flex w-full rounded-md border border-input bg-panel-elevated px-3 py-2 font-mono text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          <Alert variant="info">
            <AlertTitle>
              <Jargon term="なりすまし対策（SPF / DKIM / DMARC）">
                送信元が本物かどうかを受信側が確認するための仕組みです。DNS 設定で有効になります。
              </Jargon>
              は常に有効です
            </AlertTitle>
            <AlertDescription>
              この設定に関わらず、送信元を偽装したメールの判定は常に行われます。
              <MailWarning className="ml-1 inline size-3.5" aria-hidden />
            </AlertDescription>
          </Alert>

          {canWrite ? <SubmitBar submitLabel="保存する" pending={save.isPending} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
